import {
    RICH_TEXT_ALIGN_OPTIONS,
    RICH_TEXT_FONT_OPTIONS,
    RICH_TEXT_PRESET_OPTIONS,
} from "../../../components/RichTextOptions";

type RichTextPayloadValue = string | number | boolean | null | undefined;

export class CmsFieldEditor {
    private static readonly alignClasses = RICH_TEXT_ALIGN_OPTIONS.map((item) => item.className);
    private static readonly fontClasses = RICH_TEXT_FONT_OPTIONS.map((item) => item.utilityClass).filter((value) => value !== "");
    private static readonly colorValues = ["gray-500", "gray-600", "gray-700", "gray-800", "gray-900", "primary", "white"];
    private static readonly colorClasses = CmsFieldEditor.colorValues.map((value) => `--text-${value}`);
    private static readonly savedRanges = new WeakMap<HTMLElement, Range>();

    static textValue(field: HTMLElement): string {
        return (field.innerText ?? "").trim();
    }

    static payload(field: HTMLElement): Record<string, RichTextPayloadValue> {
        return {
            value: this.textValue(field),
            html: this.htmlValue(field),
            align: this.alignValue(field),
            size: this.presetValue(field),
            font: this.fontValue(field),
            color: this.colorValue(field),
            color_variable: field.dataset.cmsTemplateFieldColorVariable ?? "",
        };
    }

    static apply(field: HTMLElement, format: string, value = ""): void {
        field.focus({ preventScroll: true });
        this.restoreSelection(field);

        if (format === "align") {
            this.applyAlign(field, value);
        } else if (format === "type") {
            this.applyPreset(field, value);
        } else if (format === "font") {
            this.applyFont(field, value);
        } else if (format === "color") {
            this.applyColor(field, value);
        } else if (format === "color-variable") {
            this.applyColorVariable(field, value);
        } else if (format === "clean") {
            this.cleanSelection(field);
        } else {
            this.applyInlineTag(field, format);
        }

        this.rememberSelection(field);
        field.dispatchEvent(new InputEvent("input", { bubbles: true, inputType: "formatSetBlockText" }));
    }

    static rememberSelection(field: HTMLElement): void {
        const range = this.selectionRangeInside(field);
        if (!range) return;
        this.savedRanges.set(field, range.cloneRange());
    }

    static restoreSelection(field: HTMLElement): boolean {
        const range = this.savedRanges.get(field);
        if (!range || !field.contains(range.commonAncestorContainer)) return false;
        const selection = window.getSelection();
        if (!selection) return false;
        selection.removeAllRanges();
        selection.addRange(range.cloneRange());
        return true;
    }

    static hasSelection(field: HTMLElement): boolean {
        const range = this.selectionRangeInside(field);
        return !!range && !range.collapsed;
    }

    private static htmlValue(field: HTMLElement): string {
        const clone = field.cloneNode(true) as HTMLElement;
        clone.removeAttribute("contenteditable");
        clone.removeAttribute("spellcheck");
        clone.removeAttribute("tabindex");
        Array.from(clone.attributes).forEach((attr) => {
            if (attr.name.startsWith("data-cms-") || attr.name === "style") {
                clone.removeAttribute(attr.name);
            }
        });
        clone.querySelectorAll<HTMLElement>("*").forEach((node) => {
            node.removeAttribute("style");
            Array.from(node.attributes).forEach((attr) => {
                if (attr.name.startsWith("data-cms-") || attr.name.startsWith("on")) {
                    node.removeAttribute(attr.name);
                }
            });
        });
        return clone.innerHTML.trim();
    }

    static alignValue(field: HTMLElement): string {
        const found = RICH_TEXT_ALIGN_OPTIONS.find((item) => field.classList.contains(item.className));
        return found?.value ?? "";
    }

    static presetValue(field: HTMLElement): string {
        const found = RICH_TEXT_PRESET_OPTIONS.find((item) => item.classes.some((className) => field.classList.contains(className)));
        if (found) return found.value;
        const className = Array.from(field.classList).find((item) => /^no-(display|heading|body)-[a-z0-9-]+$/i.test(item));
        return className?.replace(/^no-/, "") ?? "";
    }

    static fontValue(field: HTMLElement): string {
        const found = RICH_TEXT_FONT_OPTIONS.find((item) => item.utilityClass !== "" && field.classList.contains(item.utilityClass));
        return found?.value ?? "";
    }

    static colorValue(field: HTMLElement): string {
        const explicit = field.dataset.cmsTemplateFieldColor ?? "";
        if (this.colorValues.includes(explicit)) return explicit;
        const found = this.colorValues.find((value) => field.classList.contains(`--text-${value}`));
        return found ?? "";
    }

    private static applyAlign(field: HTMLElement, value: string): void {
        field.classList.remove(...this.alignClasses);
        const option = RICH_TEXT_ALIGN_OPTIONS.find((item) => item.value === value);
        if (option) field.classList.add(option.className);
    }

    private static applyPreset(field: HTMLElement, value: string): void {
        const option = RICH_TEXT_PRESET_OPTIONS.find((item) => item.value === value);
        if (!option) return;
        this.wrapSelectionOrField(field, option.classes, "preset");
    }

    private static applyFont(field: HTMLElement, value: string): void {
        const option = RICH_TEXT_FONT_OPTIONS.find((item) => item.value === value);
        if (!option) return;
        this.wrapSelectionOrField(field, option.utilityClass !== "" ? [option.utilityClass] : [], "font");
    }

    private static applyColor(field: HTMLElement, value: string): void {
        if (!this.colorValues.includes(value)) return;
        field.classList.remove(...this.colorClasses);
        field.classList.add(`--text-${value}`);
        field.dataset.cmsTemplateFieldColor = value;
    }

    private static applyColorVariable(field: HTMLElement, value: string): void {
        if (!/^[a-z][a-z0-9_.-]{0,80}$/i.test(value)) return;
        field.dataset.cmsTemplateFieldColorVariable = value.toLowerCase();
    }

    private static applyInlineTag(field: HTMLElement, format: string): void {
        const tag = this.tagForFormat(format);
        if (tag === "") return;
        const range = this.selectionRangeInside(field);
        if (!range) return;
        if (range.collapsed) {
            this.selectFieldContents(field);
        }
        const nextRange = this.selectionRangeInside(field);
        if (!nextRange) return;
        this.wrapRange(nextRange, document.createElement(tag));
    }

    private static wrapSelectionOrField(field: HTMLElement, classes: string[], classGroup: "preset" | "font"): void {
        const range = this.selectionRangeInside(field);
        if (!range || range.collapsed) {
            this.applyClassesToField(field, classes, classGroup);
            return;
        }
        const span = document.createElement("span");
        if (classes.length > 0) span.className = classes.join(" ");
        this.wrapRange(range, span);
    }

    private static applyClassesToField(field: HTMLElement, classes: string[], classGroup: "preset" | "font"): void {
        if (classGroup === "preset") {
            this.removeMatchingClasses(field, [/^no-(display|heading|body)-[a-z0-9-]+$/i, /^--fw-[0-9]+$/]);
        } else {
            this.removeMatchingClasses(field, [/^--font-[a-z0-9-]+$/i]);
        }
        if (classes.length > 0) field.classList.add(...classes);
    }

    private static cleanSelection(field: HTMLElement): void {
        const range = this.selectionRangeInside(field);
        if (!range || range.collapsed) {
            field.classList.remove(...this.alignClasses, ...this.fontClasses);
            field.classList.remove(...this.colorClasses);
            field.dataset.cmsTemplateFieldColor = "";
            field.dataset.cmsTemplateFieldColorVariable = "";
            this.removeMatchingClasses(field, [/^no-(display|heading|body)-[a-z0-9-]+$/i, /^--fw-[0-9]+$/]);
            field.querySelectorAll<HTMLElement>("strong,b,em,i,u,s,strike,span").forEach((node) => this.unwrap(node));
            return;
        }

        const text = range.toString();
        const textNode = document.createTextNode(text);
        range.deleteContents();
        range.insertNode(textNode);
        this.restoreAfter(range, textNode);
    }

    private static wrapRange(range: Range, wrapper: HTMLElement): void {
        const fragment = range.extractContents();
        wrapper.appendChild(fragment);
        range.insertNode(wrapper);
        this.restoreAfter(range, wrapper);
    }

    private static selectionRangeInside(field: HTMLElement): Range | null {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return null;
        const range = selection.getRangeAt(0);
        if (!field.contains(range.commonAncestorContainer)) return null;
        return range;
    }

    private static selectFieldContents(field: HTMLElement): void {
        const selection = window.getSelection();
        if (!selection) return;
        const range = document.createRange();
        range.selectNodeContents(field);
        selection.removeAllRanges();
        selection.addRange(range);
    }

    private static restoreAfter(range: Range, node?: Node): void {
        const selection = window.getSelection();
        if (!selection) return;
        const next = document.createRange();
        if (node) {
            next.selectNodeContents(node);
            next.collapse(false);
        } else {
            next.setStart(range.endContainer, range.endOffset);
            next.collapse(true);
        }
        selection.removeAllRanges();
        selection.addRange(next);
    }

    private static unwrap(node: HTMLElement): void {
        const parent = node.parentNode;
        if (!parent) return;
        while (node.firstChild) parent.insertBefore(node.firstChild, node);
        parent.removeChild(node);
    }

    private static removeMatchingClasses(field: HTMLElement, patterns: RegExp[]): void {
        Array.from(field.classList).forEach((className) => {
            if (patterns.some((pattern) => pattern.test(className))) {
                field.classList.remove(className);
            }
        });
    }

    private static tagForFormat(format: string): string {
        if (format === "bold") return "strong";
        if (format === "italic") return "em";
        if (format === "underline") return "u";
        if (format === "strike") return "s";
        return "";
    }
}
